/**
 * Splitting one item between the people ticked on it.
 *
 *   Even:     nobody listed in Custom split; everyone ticked pays the same.
 *   Weights:  "Emily 2, Chloe 1" on $30 gives Emily $20 and Chloe $10.
 *             Anyone ticked but not listed counts as weight 1.
 *   Dollars:  "Emily $25" on $30 gives Emily $25; the $5 left over is split evenly
 *             among everyone ticked who isn't listed.
 *
 * All amounts are in micro (1/10,000 cent), and parts always add up to the item total.
 */
import { MICRO_PER_CENT, formatMicro, parseMoney, splitByWeights, type Micro } from "../money.ts";
import type { GroupState, Item, Person, SplitBy } from "../types.ts";

export type SplitMode = "even" | "weights" | "dollars";

export interface ItemSplit {
  /** Price + coupon + CRV, in cents (what tax applies to). */
  netCents: number;
  /** Tax rate actually used (item's own, else the store's, else 0). */
  taxBp: number;
  /** Tip rate actually used (item's own, else the store's, else 0). */
  tipBp: number;
  /** Net + tax, in micro. */
  totalMicro: Micro;
  mode: SplitMode;
  /** Each ticked person's share, in micro. */
  shares: Record<string, Micro>;
  /** Problems with the Custom split text, in plain words. */
  errors: string[];
}

export function effectiveTaxBp(state: GroupState, item: Item): number {
  return item.taxBp ?? state.stores[item.store]?.taxBp ?? 0;
}

export function effectiveTipBp(state: GroupState, item: Item): number {
  return item.tipBp ?? state.stores[item.store]?.tipBp ?? 0;
}

export function effectiveCategory(state: GroupState, item: Item): string {
  return item.category ?? state.stores[item.store]?.category ?? "Other";
}

/** Exact item total: net cents x (1 + tax). Integer math, no rounding. */
export function itemTotalMicro(netCents: number, taxBp: number): Micro {
  return netCents * MICRO_PER_CENT + netCents * taxBp;
}

interface Parsed {
  mode: SplitMode;
  /** person id -> weight (x1000, integer) or fixed amount (cents) */
  values: Map<string, number>;
  errors: string[];
}

const TOKEN = /^(.*?)\s*[:=]?\s*(\$)?\s*(\d+(?:\.\d+)?)\s*$/;

/** Reads the Custom split text against the people ticked on the item. */
export function parseCustomSplit(
  text: string,
  ticked: readonly Person[],
  everyone: readonly Person[],
): Parsed {
  const values = new Map<string, number>();
  const errors: string[] = [];
  const t = text.trim();
  if (t === "") return { mode: "even", values, errors };

  let sawDollar = false;
  let sawWeight = false;
  for (const raw of t.split(/[,;/]/)) {
    const token = raw.trim();
    if (token === "") continue;
    const m = TOKEN.exec(token);
    const name = m?.[1]?.trim() ?? "";
    if (!m || name === "") {
      errors.push(`Couldn't read "${token}". Write it like "Emily 2" or "Emily $10".`);
      continue;
    }
    const isDollar = m[2] === "$";
    const number = m[3] ?? "0";
    const person = ticked.find((p) => same(p.name, name));
    if (!person) {
      const exists = everyone.some((p) => same(p.name, name));
      errors.push(exists ? `${name} isn't ticked on this item.` : `There's nobody called ${name}.`);
      continue;
    }
    if (isDollar) {
      sawDollar = true;
      values.set(person.id, parseMoney(number) ?? 0);
    } else {
      sawWeight = true;
      values.set(person.id, Math.round(Number(number) * 1000));
    }
  }

  if (sawDollar && sawWeight) {
    errors.push("Use either weights (Emily 2) or dollars (Emily $10), not both.");
    return { mode: "even", values: new Map(), errors };
  }
  if (values.size === 0) return { mode: "even", values, errors };
  return { mode: sawDollar ? "dollars" : "weights", values, errors };
}

/**
 * The Custom split text as kept by person id (Miles, 2026-10-28): read once, when it's saved,
 * against everyone in the group, so a later rename never changes the split. null = even.
 */
export function splitByFromText(text: string, everyone: readonly Person[]): SplitBy | null {
  const parsed = parseCustomSplit(text, everyone, everyone);
  if (parsed.values.size === 0 && parsed.errors.length === 0) return null;
  return {
    mode: parsed.mode === "dollars" ? "dollars" : "weights",
    values: Object.fromEntries(parsed.values),
    ...(parsed.errors.length ? { errors: parsed.errors } : {}),
  };
}

/** The split by id, for the people ticked on the item, with the same words for problems. */
function fromSplitBy(
  by: SplitBy | null,
  ticked: readonly Person[],
  everyone: readonly Person[],
): Parsed {
  const values = new Map<string, number>();
  if (!by) return { mode: "even", values, errors: [] };
  const errors: string[] = [...(by.errors ?? [])];
  const isTicked = new Set(ticked.map((p) => p.id));
  for (const [id, v] of Object.entries(by.values)) {
    if (isTicked.has(id)) values.set(id, v);
    else {
      const p = everyone.find((x) => x.id === id);
      // Someone removed from the group is simply left out.
      if (p) errors.push(`${p.name} isn't ticked on this item.`);
    }
  }
  if (values.size === 0) return { mode: "even", values, errors };
  return { mode: by.mode, values, errors };
}

/**
 * The Custom split as words with today's names ("Emily Chen 2, Chloe 1", "Emily Chen $25.00"),
 * for showing and editing. Items from before ids show their text as typed.
 */
export function customSplitText(state: GroupState, item: Item): string {
  if (item.splitBy === undefined) return item.customSplit;
  if (item.splitBy === null) return "";
  // Text with problems is shown as typed, so the person can see and fix it.
  if (item.splitBy.errors?.length) return item.customSplit;
  const by = item.splitBy;
  const order = [
    ...state.personOrder.filter((id) => id in by.values),
    ...Object.keys(by.values).filter((id) => !state.personOrder.includes(id)),
  ];
  return order
    .filter((id) => state.people[id])
    .map((id) => {
      const v = by.values[id]!;
      const name = state.people[id]!.name;
      return by.mode === "dollars"
        ? `${name} $${(v / 100).toFixed(2)}`
        : `${name} ${String(Math.round((v / 1000) * 1000) / 1000)}`;
    })
    .join(", ");
}

export function splitItem(state: GroupState, item: Item): ItemSplit {
  const netCents = item.priceCents + item.couponCents + item.crvCents;
  const taxBp = effectiveTaxBp(state, item);
  const tipBp = effectiveTipBp(state, item);
  // Tax and tip are both on the pre-tax amount, so the total is net x (1 + tax + tip).
  const totalMicro = itemTotalMicro(netCents, taxBp + tipBp);

  // Columns are in the group's person order, so leftover micro goes to the same person
  // every time, no matter what order the boxes were ticked in.
  const tickedSet = new Set(item.ticked);
  const ticked = state.personOrder
    .filter((id) => tickedSet.has(id))
    .map((id) => state.people[id])
    .filter((p): p is Person => p !== undefined);
  const everyone = state.personOrder
    .map((id) => state.people[id])
    .filter((p): p is Person => p !== undefined);

  // Kept by id (items saved since ids): renames can't change it. Older items: read by name.
  const parsed =
    item.splitBy !== undefined
      ? fromSplitBy(item.splitBy, ticked, everyone)
      : parseCustomSplit(item.customSplit, ticked, everyone);
  const shares: Record<string, Micro> = {};
  const errors = [...parsed.errors];
  if (ticked.length === 0)
    return { netCents, taxBp, tipBp, totalMicro, mode: parsed.mode, shares, errors };

  if (parsed.mode === "dollars") {
    let fixedSum = 0;
    const rest: Person[] = [];
    for (const p of ticked) {
      const cents = parsed.values.get(p.id);
      if (cents === undefined) rest.push(p);
      else {
        shares[p.id] = cents * MICRO_PER_CENT;
        fixedSum += cents * MICRO_PER_CENT;
      }
    }
    const left = totalMicro - fixedSum;
    if (rest.length > 0) {
      if (left < 0) {
        errors.push(
          `Custom amounts add up to ${formatMicro(fixedSum)}, more than the item's ${formatMicro(totalMicro)}.`,
        );
      }
      const parts = splitByWeights(
        left,
        rest.map(() => 1),
      );
      rest.forEach((p, i) => (shares[p.id] = parts[i] ?? 0));
    } else if (Math.abs(left) > MICRO_PER_CENT / 2) {
      errors.push(
        `Custom amounts add up to ${formatMicro(fixedSum)}, but the item is ${formatMicro(totalMicro)}.`,
      );
    }
    return { netCents, taxBp, tipBp, totalMicro, mode: "dollars", shares, errors };
  }

  const weights = ticked.map(
    (p) => parsed.values.get(p.id) ?? (parsed.mode === "weights" ? 1000 : 1),
  );
  const parts = splitByWeights(totalMicro, weights);
  ticked.forEach((p, i) => (shares[p.id] = parts[i] ?? 0));
  return { netCents, taxBp, tipBp, totalMicro, mode: parsed.mode, shares, errors };
}

function same(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
